import React, { useEffect, useState } from 'react';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import {
  getProductLineInterestValueDisplayName,
  INTEREST_SELECTION_OPTIONS,
} from 'global/utils/election/productLineElection';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { useFirstRender } from '@insurancemenu/imenu-components-lib';
import { productLineActionButtonConfig } from 'global/utils/election/productLineActionButtonConfig';
import { ProductLineInterestSelection } from 'types/electionService';
import { ProductLineInterestCardProps } from '../../types';
import DependentsSection from '../../../common/DependentsSection';
import {
  ProductLineCardStyled,
  InfoSectionStyled,
  RateSectionStyled,
  TooltipStyled,
  ActionButtonStyled,
} from './styles';
import {
  SUB_PRODUCT_LINE_CARD_TEST_ID,
  SUB_NOT_EDITABLE_PRODUCT_SELECT_TEST_ID,
  SUB_RATE_BUTTON_TEST_ID,
} from '../../../constants';
import ProductLineInterestSelect from './ProductLineInterestSelect';

const ProductLineInterestCard: React.FC<ProductLineInterestCardProps> = (props) => {
  const {
    subscriberId,
    dependents,
    isEditable,
    currentInterestSelection: { productLineId, value, dependentIds },
    isValid,
    showInterestSelectionButton,
    productLineType,
    formId,
    onProductLineInterestChange,
    onDependentsChange,
    onProductLineAction,
  } = props;

  const [selectedDependentIds, setSelectedDependentIds] = useState(dependentIds || []);
  const firstRender = useFirstRender();

  const rateButtonConfig = productLineActionButtonConfig[productLineType]?.[isValid ? 'valid' : 'invalid'] || null;

  useEffect(() => {
    if (!firstRender && onDependentsChange) {
      const info = { subscriberId, productLineId, dependentIds: selectedDependentIds };
      onDependentsChange(info);
    }
  }, [selectedDependentIds.length]);

  const onInterestSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onProductLineInterestChange?.({
      subscriberId,
      productLineId,
      selection: e.target.value as ProductLineInterestSelection,
    });
  };

  const onDependentChange = (dependentId: string): void => {
    if (selectedDependentIds.includes(dependentId)) {
      const updatedDependents = selectedDependentIds.filter((id) => id !== dependentId);
      setSelectedDependentIds(updatedDependents);
    } else {
      setSelectedDependentIds([...selectedDependentIds, dependentId]);
    }
  };

  const onProductLineActionClick = (): void => {
    onProductLineAction?.(formId || '');
  };

  return (
    <ProductLineCardStyled
      $isInvalid={!isValid}
      $pLineType={productLineType}
      data-test-id={`${SUB_PRODUCT_LINE_CARD_TEST_ID}-${productLineId}`}
    >
      <InfoSectionStyled>
        {isEditable ? (
          <ProductLineInterestSelect
            subscriberId={subscriberId}
            productLineId={productLineId}
            value={value}
            options={INTEREST_SELECTION_OPTIONS}
            isEditable={isEditable}
            onInterestSelectionChange={onInterestSelectionChange}
          />
        ) : (
          <TooltipStyled
            label={getProductLineInterestValueDisplayName(value)}
            alignment={AlignmentTypes.BOTTOM_START}
            data-test-id={SUB_NOT_EDITABLE_PRODUCT_SELECT_TEST_ID}
          >
            <ProductLineInterestSelect
              subscriberId={subscriberId}
              productLineId={productLineId}
              value={value}
              options={INTEREST_SELECTION_OPTIONS}
              isEditable={isEditable}
              onInterestSelectionChange={onInterestSelectionChange}
            />
          </TooltipStyled>
        )}

        {/* Dependents section */}
        <DependentsSection
          areDependentsIneligible={
            productLineType === 'WAIVED' || PRODUCT_LINES_CONTENT_MAP[productLineId]?.hideDependents
          }
          dependentsList={dependents}
          isEditable={isEditable}
          onDependentChange={onDependentChange}
          selectedDependentIds={selectedDependentIds}
          productLineId={productLineId}
        />
      </InfoSectionStyled>

      {/* Rate section */}
      <RateSectionStyled>
        {showInterestSelectionButton && rateButtonConfig && formId && (
          <ActionButtonStyled
            type="text"
            uppercase
            color="primary"
            leftIconProp={rateButtonConfig.icon}
            onClick={onProductLineActionClick}
            data-test-id={SUB_RATE_BUTTON_TEST_ID}
          >
            {rateButtonConfig.title}
          </ActionButtonStyled>
        )}
      </RateSectionStyled>
    </ProductLineCardStyled>
  );
};

export default ProductLineInterestCard;
